import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { DetectionEntity } from '../types';
import { EntityWithFieldMetadata } from '../../UI/dynamicUI/types';

type DeleteEntityVariables = {
  clientId: string;
  entityType: string;
  entityId: string;
};

export function useDeleteEntities() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isError,
    isPending: isLoading,
    reset,
  } = useMutation<unknown, Error, DeleteEntityVariables>({
    mutationFn: ({ clientId, entityType, entityId }) =>
      api.delete(`clients/${clientId}/entity-types/${entityType}/entities/${entityId}`),
    onSuccess: (_data, { clientId, entityType, entityId }) => {
      queryClient.removeQueries({
        queryKey: ['clients', clientId, entityType, 'entities', entityId],
      });
      queryClient.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });

  const deleteEntities = async (entities: DetectionEntity[], clientId: string) => {
    const deleteRequests = entities.map(entity => {
      const entityId = entity.fields.id?.toString();
      if (!entityId) {
        return;
      }
      return mutateAsync({ clientId, entityType: entity.entityType, entityId });
    });
    await Promise.all(deleteRequests);
  };

  const deleteEntityByTypeAndId = async (
    clientId: string,
    entityType: string,
    entityId: string,
  ) => {
    if (!entityId) {
      return;
    }
    return mutateAsync({ clientId, entityType, entityId });
  };

  const deleteEntity = async (entity: EntityWithFieldMetadata, clientId: string) => {
    const entityId = entity.fields.id?.toString();
    if (!entityId) {
      return;
    }
    return deleteEntityByTypeAndId(clientId, entity.entityType, entityId);
  };

  return {
    deleteEntities,
    deleteEntity,
    deleteEntityByTypeAndId,
    isError,
    isLoading,
    reset,
  };
}
